/* eslint-disable @typescript-eslint/require-array-sort-compare */
import React, { useState, useCallback, useEffect, Fragment, ChangeEvent, useMemo } from 'react';

import {
  setFeatureFlagOverride,
  getFeatureFlagOverrides,
} from '@zola-helpers/client/dist/es/featureFlags';

import NAV_DATA, {
  FeatureFlagsAvailableEventPayload,
  FeatureFlagsAvailableFlagSettings,
} from '@zola-helpers/client/dist/es/constants/navConstants';

import CheckBoxField from '../Form/CheckboxField/CheckboxField';
import InputFieldV2 from '../Form/InputFieldV2/InputFieldV2';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore File not yet converted to TS

import styles from './FeatureToggle.module.less';
import { ButtonV3 } from '../ButtonV3';
import DropdownV3 from '../Form/dropdownV3';

type FeatureFlagOption = { value: string; label: string };

const {
  EVENTS: { FEATURE_FLAGS_AVAILABLE },
} = NAV_DATA;

const EXCLUDE_FLAGS = ['datadog', 'featureToggle', 'paypalSandbox'];

export type FeatureFlagsByDomain = {
  [domain: string]: FeatureFlagsAvailableFlagSettings;
};

export const useFeatureFlags = ({
  product,
  productFlags,
}: {
  product: string;
  productFlags: FeatureFlagsAvailableFlagSettings;
}): FeatureFlagsByDomain => {
  /**
   * A lookup of domains (products) to feature flag information (value: boolean, production:
   * boolean, staging: boolean, development: boolean)  that gives us the flexibility to add
   * tickets or descriptions to the information in the future for better display / usefulness with qa
   */
  const [featureFlagsByDomain, setFeatureFlagsByDomain] = useState<FeatureFlagsByDomain>({});

  useEffect(() => {
    if (!Object.keys(featureFlagsByDomain).includes(product)) {
      setFeatureFlagsByDomain({
        ...featureFlagsByDomain,
        [product]: productFlags,
      });
    }
  }, [productFlags, product, featureFlagsByDomain]);

  /**
   *
   * Listener for the domains (products) firing a FEATURE_FLAGS_AVAILABLE
   * event that provides the list of feature flags for the product
   */
  useEffect(() => {
    const handleFeatureFlagsLoaded = (event: CustomEvent<FeatureFlagsAvailableEventPayload>) => {
      const {
        detail: { domain, flags },
      } = event;

      const newFlagsByDomain = {
        ...featureFlagsByDomain,
        [domain]: flags,
      };

      setFeatureFlagsByDomain(newFlagsByDomain);
    };

    window.addEventListener(FEATURE_FLAGS_AVAILABLE, handleFeatureFlagsLoaded as EventListener);
    return () => {
      window.removeEventListener(
        FEATURE_FLAGS_AVAILABLE,
        handleFeatureFlagsLoaded as EventListener
      );
    };
  }, [featureFlagsByDomain]);

  return featureFlagsByDomain;
};

export const FeatureFlags = ({
  featureFlagsByDomain,
}: {
  featureFlagsByDomain: FeatureFlagsByDomain;
}): JSX.Element => {
  const [featureFlag, setFeatureFlag] = useState('');
  const [featureFlagValue, setFeatureFlagValue] = useState<boolean | null>(null);

  /** If true, exclude feature flags that have been enabled in production from the drop down */
  const [excludeProductionFlags, setExcludeProductionFlags] = useState(true);

  /**
   * label/value paris for the feature flag drop down
   */
  const [featureFlagOptions, setFeatureFlagOptions] = useState<FeatureFlagOption[]>([]);

  const submitFeatureFlagOverride = () => {
    if (!featureFlag || featureFlagValue === null) {
      return;
    }

    setFeatureFlagOverride(featureFlag, featureFlagValue);
    window.location.reload();
  };

  const featureFlagOverrides = getFeatureFlagOverrides();

  const allFlagsSetToProduction = useMemo(() => {
    return Object.keys(featureFlagsByDomain).every((domain) => {
      const flagsForDomain = Object.keys(featureFlagsByDomain[domain]);

      return flagsForDomain
        .filter((flag) => !EXCLUDE_FLAGS.includes(flag))
        .every((flag) => {
          const flagSettings = featureFlagsByDomain[domain][flag];
          return flagSettings.production === flagSettings.value;
        });
    });
  }, [featureFlagsByDomain]);

  /**
   * Overrides all feature flags to their production values.
   */
  const handleSetFeatureFlagsToProductionValues = useCallback(() => {
    if (allFlagsSetToProduction) {
      Object.keys(featureFlagOverrides).forEach((flag) => {
        setFeatureFlagOverride(flag, !featureFlagOverrides[flag]);
      });
    } else {
      Object.keys(featureFlagsByDomain).forEach((domain) => {
        const flagsForDomain = Object.keys(featureFlagsByDomain[domain]);

        flagsForDomain
          .filter((flag) => !EXCLUDE_FLAGS.includes(flag))
          .forEach((flag) => {
            const flagSettings = featureFlagsByDomain[domain][flag];

            if (flagSettings.production !== flagSettings.value) {
              setFeatureFlagOverride(flag, flagSettings.production);
            }
          });
      });
    }
    window.location.reload();
  }, [featureFlagsByDomain, allFlagsSetToProduction, featureFlagOverrides]);

  /**
   * Whenever we get new feature flags, compute the drop down options
   */
  useEffect(() => {
    const domains = Object.keys(featureFlagsByDomain).sort();

    let options: FeatureFlagOption[] = [];

    domains.forEach((domain) => {
      const flagsForDomain = featureFlagsByDomain[domain];

      const optionsForDomain = Object.keys(flagsForDomain)
        // filter out (or not) feature flgas that are on in production
        .filter((flag) => (excludeProductionFlags ? !flagsForDomain[flag].production : true))
        .sort()
        .map((flag) => {
          return {
            value: flag,
            // This is the text display for the dropdown
            label: `${flag} (${flagsForDomain[flag].value}) - ${domain}`,
          };
        });

      options = [...options, ...optionsForDomain];
    });

    setFeatureFlagOptions(options);
  }, [featureFlagsByDomain, excludeProductionFlags]);

  return (
    <div className={styles.featureFlags}>
      {Object.keys(featureFlagsByDomain).length === 0 ? (
        <InputFieldV2
          type="text"
          label="Feature Flag"
          value={featureFlag}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setFeatureFlag(e.target.value)}
        />
      ) : (
        <>
          <DropdownV3
            id="featureFlagList"
            options={featureFlagOptions}
            label="Feature Flag"
            className={styles.dropdown}
            value={featureFlag}
            onSelect={setFeatureFlag}
            inline
          />
          <CheckBoxField
            id="excludeProductionFlags"
            inline
            label="Exclude production flags"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setExcludeProductionFlags(e.target.checked)
            }
            checked={excludeProductionFlags}
          />
          <hr />
        </>
      )}
      <DropdownV3
        id="featureFlagValue"
        options={[
          { label: 'True', value: 'true' },
          { label: 'False', value: 'false' },
        ]}
        label="Value"
        onSelect={(value) => setFeatureFlagValue(value === 'true')}
        value={featureFlagValue === null ? undefined : `${featureFlagValue}`}
        nullable
      />
      <div>
        <label>Current Overrides</label>

        <ul style={{ maxHeight: '200px', overflowY: 'auto' }}>
          {Object.keys(featureFlagOverrides).map((key) => (
            <li key={key}>
              {key}: {JSON.stringify(featureFlagOverrides[key])}
            </li>
          ))}
        </ul>
      </div>
      <ButtonV3 onClick={submitFeatureFlagOverride}>
        Override Feature Flag Override And Reload
      </ButtonV3>

      <div>
        <CheckBoxField
          id="overrideAllFlags"
          label="Set all feature flags to production values"
          onChange={handleSetFeatureFlagsToProductionValues}
          checked={allFlagsSetToProduction}
        />
      </div>
    </div>
  );
};
